import moment from 'moment';
import 'moment-timezone';
import { Expiry } from '..';
import { addHours, addMinutes } from 'date-fns';

const DubaiTimezone = 'Asia/Dubai';

export const formatDate = (date: string | Date, dateFormat: string): string => {
  return moment(date).format(dateFormat);
};
export const formatUTCDate = (
  date: string | Date,
  dateFormat: string
): string => {
  return moment(date).utc().format(dateFormat);
};

export const convertToTimeZoneByKeepingSameDate = (
  date: string | Date,
  timezone: string = DubaiTimezone
) => {
  return moment.tz(
    formatDate(date, DateFormats.NormalDateTimeFormat),
    DateFormats.NormalDateTimeFormat,
    timezone
  );
};

export const convertToTimezone = (
  date: Date,
  timezone: string = DubaiTimezone
) => {
  return moment(date).tz(timezone).toDate();
};

export const formatDateWithTimeZone = (
  date: string | Date,
  timezone: string = DubaiTimezone,
  dateFormat: string = DateFormats.NormalDateTimeFormat
) => {
  return moment(date).tz(timezone).format(dateFormat);
};

export const tryFormatDate = (
  date: string | Date,
  dateFormat: string = DateFormats.NormalDateTimeFormat
): string | Date => {
  try {
    return formatDate(date, dateFormat);
  } catch (e) {
    return date;
  }
};

export const tryFormatDateWithTimezone = (
  date: string | Date,
  timezone: string = DubaiTimezone,
  dateFormat: string = DateFormats.NormalDateTimeFormat
): string | Date => {
  try {
    return formatDateWithTimeZone(date, timezone, dateFormat);
  } catch (e) {
    return date;
  }
};
export const timeBuilder = (minDate?: Date, gap?: number) => {
  const date = minDate ?? new Date();
  if (gap === undefined) {
    gap = 5;
  }
  let start = moment(date).startOf('day').toDate();
  if (minDate) {
    start = moment(minDate).add(gap, 'minute').toDate();
  }
  const end = moment(date).endOf('day').toDate();
  const timeArray = [];
  while (
    moment(end).isAfter(moment(start)) &&
    !moment(end).isSame(moment(start))
  ) {
    const formattedTime = formatDate(start, DateFormats.TimeOnlyFormat);
    const object = {
      label: formattedTime + timeDifference(start, minDate),
      value: formattedTime,
    };
    timeArray.push(object);
    start = moment(start).add('minute', gap).toDate();
  }
  return timeArray;
};

export const singleDayTimeBuilder = (minDate?: Date, gap?: number) => {
  const date = minDate ?? new Date();
  let end = moment(date).endOf('day').toDate();

  if (gap === undefined) {
    gap = 5;
  }
  let start = moment(date).startOf('day').toDate();
  if (minDate) {
    start = moment(minDate).add(gap, 'minute').toDate();
    end = moment(minDate).endOf('day').toDate();
  }
  const timeArray = [];
  while (
    moment(end).isAfter(moment(start)) &&
    !moment(end).isSame(moment(start))
  ) {
    const formattedTime = formatDate(start, DateFormats.TimeOnlyFormat);
    const object = {
      label: formattedTime + timeDifference(start, minDate),
      value: formattedTime,
    };
    timeArray.push(object);
    start = moment(start).add('minute', gap).toDate();
  }
  return timeArray;
};

export const timeDifference = (from?: Date, then?: Date) => {
  if (from == null || then == null) {
    return '';
  }
  const momentFrom = moment(from);
  const momentThen = moment(then);
  if (momentFrom.isBefore(momentThen)) {
    return '';
  }
  const duration = moment.duration(momentFrom.diff(momentThen));
  const minutes = duration.asMinutes();
  let remainingMinutes = minutes;
  let hours = 0;
  if (minutes > 60) {
    remainingMinutes = minutes % 60;
    hours = Math.trunc(minutes / 60);
  }
  let returnValue = '';
  if (hours > 0) {
    returnValue = `${hours} h `;
    if (remainingMinutes > 0) {
      returnValue += `${remainingMinutes} min`;
    }
  } else {
    returnValue = `${minutes} min`;
    if (minutes === 0) {
      return '';
    }
  }

  return ` (${returnValue})`;
};

export class DateFormats {
  static NormalDateTimeFormat = 'DD-MMM-YYYY hh:mm A';
  static NormalDateFormat = 'DD-MMM-YYYY';
  static NormalDateFormatWithoutHiphons = 'DD MMM YYYY';
  static TimeWithDayName = 'hh:mm A, DD MMM ';
  static MonthNameOnly = 'MMM';
  static TimeOnlyFormat = 'hh:mm A';
  static DayNameOnly = 'ddd';
  static MonthWithDay = 'MMM DD';
  static NormalDateFormatWith24Hour = 'DD MMM YYYY HH:mm';
  static TimeOnly24HourFormat = 'HH:mm';
}

export const addDays = (date: Date, days: number) => {
  return moment(date).add(days, 'days').toDate();
};

export const getUTCStartOfDate = (date: Date): Date => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
};

export const getDayDifference = (start?: Date, end?: Date) => {
  if (start == null || end == null) {
    return 0;
  }
  return moment(end).diff(moment(start), 'days');
};

export const getMinuteDifference = (start?: Date, end?: Date) => {
  if (start == null || end == null) {
    return 0;
  }
  return moment(end).diff(moment(start), 'minutes');
};

export const createStartOfDayWithTimeZone = (date: Date) => {
  return moment(moment(date, DateFormats.NormalDateFormat).utc(true))
    .utc(true)
    .toDate();
};

export const getMinutes = (exp: Expiry) => {
  switch (exp?.unit) {
    case 'Days':
      return exp.value * 24 * 60;
    case 'Hours':
      return exp.value * 60;
    case 'Minutes':
      return exp.value;
    default:
      return 0;
  }
};

export const getExpiry = (exp: Expiry) => {
  switch (exp?.unit) {
    case 'Days':
      return addDays(new Date(), exp.value);
    case 'Hours':
      return addHours(new Date(), exp.value);
    case 'Minutes':
      return addMinutes(new Date(), exp.value);
    default:
      return new Date();
  }
};

export const convertToUtcConsideringTimezone = (date: Date) => {
  return addMinutes(date, -1 * new Date(date).getTimezoneOffset());
};

export const getNearestFutureTime = (currentTime: string, timeGap: number) => {
  const currentMoment = moment(currentTime, DateFormats.TimeOnlyFormat);

  if (currentMoment.minute() % timeGap === 0) {
    return currentTime;
  }

  const minutesToNextMultiple = timeGap - (currentMoment.minute() % timeGap);

  const futureTime = currentMoment.clone().add(minutesToNextMultiple, "minutes");

  return futureTime.format(DateFormats.TimeOnlyFormat);
  
};